// The note's two blocks that come BEFORE the step sections (PROD-2011).
//
// Flutter's encounter note is not just the step sections — `generated_notes_widget.dart:90-116`
// builds, in order:
//
//   1. generatedNotesHeader()  — patient identity · "location • date • time" · Created by <provider>
//   2. reasonForVisit()        — the reason, and INSIDE it `VitalsComponentWidget` (:344-365)
//   3. DividerLine()
//   4. patientSummary()        — a "PATIENT SUMMARY" table of the summary clusters (:429)
//   5. notesWidget()           — HPI · Risk · ROS · PE · Docs · A/P
//
// (1) and the reason text ride the DS `GeneratedNote` header props. This module builds (2)'s
// vitals and (4)'s patient summary as ordinary `NoteSection`s, so the same DS renderer draws
// them: heading + label/value rows, which is exactly what Flutter's
// `VitalsNoteComponentWidget` / `TableInfoWidget` draw.
import type { IpsSection } from '@scripsteam/scrips-signal-ds';
import type { PatientVital } from '@/types/vitals-api';
import { patientVitalsToRow } from './to-vitals-row';
import type { NoteSection } from '../generated-note';

/**
 * The note's VITALS rows — Flutter's `emptyVitalUiList` (`ui_data.dart:1184-1291`), in its order,
 * with its own `title` (NOT `shortTitle`: the note renders `title`, so it reads "Temperature" while
 * the tile reads "Temp" — `vital_data.dart:117-123`) and its own `symbol`.
 *
 * "02 Sat" keeps Flutter's zero rather than the letter O. This is a clinical record; matching the
 * source beats tidying it.
 *
 * `paired` marks the two-value rows, whose absent form is "— /— " rather than "— " + symbol
 * (`display()`, `vital_data.dart:49-65`). BP is systolic/diastolic; Visual Acuity and Hearing Test
 * are OS/OD.
 */
const VITAL_ROWS: { key: string; title: string; symbol: string; paired?: boolean }[] = [
  { key: 'temp', title: 'Temperature', symbol: '°C' },
  { key: 'pulse', title: 'Pulse', symbol: '' },
  { key: 'bp', title: 'BP', symbol: '', paired: true },
  { key: 'respRate', title: 'Resp Rate', symbol: '' },
  { key: 'o2Sat', title: '02 Sat', symbol: '%' },
  { key: 'height', title: 'Height', symbol: 'cm' },
  { key: 'weight', title: 'Weight', symbol: 'kg' },
  { key: 'bmi', title: 'BMI', symbol: '' },
  { key: 'hipCirc', title: 'Hip Circumference', symbol: 'cm' },
  { key: 'waistCirc', title: 'Waist Circumference', symbol: 'cm' },
  { key: 'waistToHipRatio', title: 'Waist-to-Hip Ratio', symbol: '' },
  { key: 'bioage', title: 'Bioage', symbol: '' },
  { key: 'visualAcuity', title: 'Visual Acuity', symbol: '', paired: true },
  { key: 'hearingTest', title: 'Hearing Test', symbol: '', paired: true },
];

/** Flutter's own missing-value forms (`vital_data.dart:49-65`). Note the trailing space. */
const ABSENT = '— ';
const ABSENT_PAIRED = '— /— ';

/** `isVitalReviewed ? S.current.vitalsReviewed : 'Vitals need to be reviewed'` (:55-60). */
export const VITALS_REVIEWED_LINE = 'Vitals Reviewed';
export const VITALS_NEED_REVIEW_LINE = 'Vitals need to be reviewed';

export interface VitalsNoteOptions {
  /** Drives the gender-conditional LMP row. */
  sex?: 'M' | 'F' | 'X';
  /** Whole years — drives the LMP (>8) and Head Circumference (<=2) rows. */
  ageYears?: number;
  /** Flutter's `isVitalReviewed`; picks which review line follows the table. */
  reviewed?: boolean;
}

export const VITALS_NOTE_TITLE = 'Vitals';
export const PATIENT_SUMMARY_NOTE_TITLE = 'Patient Summary';

/**
 * The note's VITALS block — Flutter's `VitalsComponentWidget` inside `reasonForVisit`
 * (`generated_notes_widget.dart:360-364` -> `vital_component_widget.dart:34-63`).
 *
 * THE BLOCK IS ALWAYS PRESENT, AND EVERY ROW IS ALWAYS PRESENT. This CORRECTS the first cut of
 * this function, which dropped unrecorded rows and returned `[]` when nothing was recorded, on the
 * reading that `VitalsNoteComponentWidget` filters `description.isNotEmpty` and returns
 * `Container()` for an empty list (`vitals_note_component_widget.dart:27-31`). That filter is real
 * but never fires: `display()` returns "— " for a missing reading (`vital_data.dart:49-65`), and an
 * em dash is not an empty string — so every row survives and the list is never empty. Andrew's
 * Flutter screenshot (2026-08-24) shows exactly that: the full table with "—" against each
 * unrecorded vital.
 *
 * Flutter builds the row set from `emptyVitalUiList(gender:, age:)`, so two rows are CONDITIONAL on
 * the patient and are a meaning error if shown otherwise (`ui_data.dart:1258-1291`):
 *   · LMP                — female AND age > 8
 *   · Head Circumference — age <= 2
 * Both are omitted when the inputs are unknown: guessing a patient's sex to render a menstrual
 * field is worse than leaving it out.
 *
 * A review line follows the table, which is Flutter's own copy either way (:55-60).
 */
export function vitalsNoteSection(
  vitals: PatientVital[] | undefined,
  opts?: VitalsNoteOptions,
): NoteSection[] {
  const row = patientVitalsToRow(vitals) as Record<
    string,
    { value?: string | number | null; unit?: string } | undefined
  >;

  const rows = [...VITAL_ROWS];
  // Flutter appends these AFTER the base list, in this order.
  if (opts?.sex === 'F' && (opts?.ageYears ?? -1) > 8) {
    rows.push({ key: 'lmp', title: 'LMP', symbol: '' });
  }
  if (opts?.ageYears != null && opts.ageYears <= 2) {
    rows.push({ key: 'headCirc', title: 'Head Circumference', symbol: 'cm' });
  }

  const categories = rows.map((r) => {
    const value = row[r.key]?.value;
    const missing = value == null || `${value}`.trim() === '';
    // Flutter: `info() = display() + symbol`, with no space between value and symbol.
    const text = missing
      ? r.paired
        ? ABSENT_PAIRED
        : `${ABSENT}${r.symbol}`
      : `${value}${r.symbol}`;
    return { title: r.title, lines: [text] };
  });

  categories.push({
    // No label: Flutter's review line is a standalone sentence under the table, not a keyed row.
    title: '',
    lines: [opts?.reviewed ? VITALS_REVIEWED_LINE : VITALS_NEED_REVIEW_LINE],
  });

  return [
    {
      section: VITALS_NOTE_TITLE,
      lines: categories.flatMap((c) => c.lines),
      categories,
    },
  ];
}

/**
 * The note's PATIENT SUMMARY block — Flutter's `patientSummary()` (`:429`).
 *
 * Flutter drops patient FLAGS from the list before rendering
 * (`removeWhere((e) => e.id == AppConstants.patientFlagsId)`), so this does too: flags are an
 * alert surface, not note content. Every other cluster becomes a row keyed by its title.
 *
 * Empty clusters are skipped — a note that lists "Allergies:" with nothing after it reads as an
 * assertion of no allergies, which is a claim this function is not entitled to make.
 */
export function patientSummaryNoteSection(sections: IpsSection[] | undefined): NoteSection[] {
  const categories = (sections ?? [])
    .filter((s) => !isFlagsSection(s))
    .flatMap((s) => {
      const lines = (s.entries ?? [])
        .map((e) => [e.primary, e.secondary].filter(Boolean).join(' — ').trim())
        .filter((l) => l.length > 0);
      return lines.length > 0 ? [{ title: s.title ?? s.id, lines }] : [];
    });
  if (categories.length === 0) return [];
  return [
    {
      section: PATIENT_SUMMARY_NOTE_TITLE,
      lines: categories.flatMap((c) => c.lines),
      categories,
    },
  ];
}

/** Flags are matched on id first (the stable handle) and only then on title. */
function isFlagsSection(s: IpsSection): boolean {
  const id = (s.id ?? '').toLowerCase();
  if (id.includes('flag')) return true;
  return (s.title ?? '').toLowerCase().includes('flag');
}
